import { useEffect, useMemo, useRef } from "react";
import { lyricBlocks, sectionAt, sectionTimeline } from "@/lib/arrangement";

const SINGER = {
  lead: { label: "You", cls: "border-amber-300/50 text-amber-100" },
  partner: { label: "Partner", cls: "border-sky-300/50 text-sky-100" },
  both: { label: "Together", cls: "border-violet-300/50 text-violet-100" },
  choir: { label: "Choir", cls: "border-emerald-300/50 text-emerald-100" },
  call_response: { label: "You call · choir answers", cls: "border-violet-300/50 text-violet-100" },
};
const YOURS = new Set(["lead", "both", "call_response", null]);

/** Lyrics on screen while you sing — the section being sung lights up (the next one glows during intros/breaks). */
export default function LyricsPrompter({ lyrics, backing, playhead, compact = false, testId = "lyrics-prompter" }) {
  const blocks = useMemo(() => lyricBlocks(lyrics, backing?.parts), [lyrics, backing?.parts]);
  const timeline = useMemo(() => sectionTimeline(backing), [backing]);
  const active = timeline.length ? sectionAt(timeline, playhead) : -2;
  const upcoming = active === -1 ? timeline.find((s) => s.start > playhead)?.index ?? null : null;
  const refs = useRef({});
  const focus = active >= 0 ? active : upcoming;
  useEffect(() => { if (focus != null) refs.current[focus]?.scrollIntoView?.({ block: "nearest", behavior: "smooth" }); }, [focus]);

  if (!blocks.length) return <p data-testid={testId === "lyrics-prompter" ? "prompter-empty" : `${testId}-empty`} className="text-[13px] text-white/45">This song has no lyrics to show.</p>;
  return (
    <div data-testid={testId} className={`${compact ? "max-h-[34vh]" : "max-h-[62vh]"} space-y-3 overflow-y-auto pr-1`}>
      {blocks.map((b) => {
        const on = b.index === active;
        const next = b.index === upcoming;
        const s = SINGER[b.singer];
        const mine = YOURS.has(b.singer);
        return (
          <div key={b.index} ref={(el) => { refs.current[b.index] = el; }} data-testid={`${testId === "lyrics-prompter" ? "prompter" : testId}-block-${b.index}`} data-active={on ? "1" : next ? "next" : "0"}
            className={`rounded-2xl border px-4 py-3 transition-[background-color,border-color,opacity] duration-300 ${on ? "border-rose-300/50 bg-rose-500/[0.12]" : next ? "border-dashed border-amber-300/50 bg-amber-500/[0.06]" : "border-white/[0.06] bg-white/[0.02]"} ${mine ? "" : "opacity-55"}`}>
            <p className="mb-1.5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">
              {b.label}
              {s ? <span className={`rounded-full border px-1.5 py-px text-[9px] ${s.cls}`}>{s.label}</span> : null}
              {next ? <span className="text-amber-200">Coming up</span> : null}
            </p>
            {b.lines.map((ln, i) => <p key={i} className={`leading-relaxed ${on ? "text-[17px] text-white" : "text-[15px] text-white/75"}`}>{ln}</p>)}
          </div>
        );
      })}
    </div>
  );
}
